import { SessionState, VitalSigns, VitalSignsResults } from "@binah/web-sdk";
import { useMutation, useQuery } from "@tanstack/react-query";
import Axios, { AxiosError } from "axios";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import ScanErrorDialog from "components/dialogs/ScanErrorDialog";
import Loader from "components/Loader";
import useCameras from "hooks/useCameras";
import useError from "hooks/useError";
import usePageVisibility from "hooks/usePageVisibility";
import usePrevious from "hooks/usePrevious";
import useTimer from "hooks/useTimer";
import { formatHealthReading } from "lib/health-readings";
import { getBaseUrl, getCurrentTimestamp } from "lib/utils";
import { PATH_ROUTES } from "routes/path";
import { useMutateBinahSettings } from "services/face-scan/hooks";
import { BinahSetting } from "services/face-scan/type";
import { USER_ACTIVITY, UserAttributes } from "../../../types";
import Scan from "./Scan";
import FaceScanResult from "./ScanResult";
import useMonitor from "./useMonitor";
import ValidationErrorDialog from "./ValidationErrorDialog";

export type ValidityCount = {
  [key: string]: number;
};

const apiClient = Axios.create({
  baseURL: getBaseUrl() + process.env.REACT_APP_API_VERSION,
});

interface VerifySessionProps {
  session_id: string;
  profile_id: string;
  locale: string;
}
const verifySession = async ({
  session_id,
  profile_id,
  locale,
}: VerifySessionProps) => {
  return await apiClient.get<{ token: string; error: string }>(
    "/verify-session",
    { params: { session_id, profile_id, locale }, withCredentials: true }
  );
};

export default function MobileScan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get("profile_id") ?? "";
  const sessionId = searchParams.get("session_id");
  const locale = searchParams.get("locale") ?? "en-us";

  const cameras = useCameras();
  const [cameraId, setCameraId] = useState<string>();

  useEffect(() => {
    if (!cameras?.length) return;
    setCameraId(cameras[0].deviceId);
  }, [cameras]);

  const videoRef = useRef<HTMLVideoElement>(null);

  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingTimeoutPromise, setLoadingTimeoutPromise] = useState<number>();
  const [__, setIsMeasurementEnabled] = useState<boolean>(false);
  const [finalResults, setFinalResults] = useState<VitalSigns | null>(null);
  const [imageValidityJSON, setImageValidityJSON] = useState<ValidityCount>({});
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [isPostingData, setIsPostingData] = useState<boolean>(false);
  const [showScanResult, setShowScanResult] = useState<boolean>(false);
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
  const [processingTime, setProcessingTime] = useState(0);
  const [includeDemographic, setIncludeDemographic] = useState<boolean>(false);
  const [licenseKey, setLicenseKey] = useState("");

  const { mutateAsync: fetchBinahSetting, isPending: isSDKFetching } =
    useMutateBinahSettings({
      onSuccess: (data) => {
        storeBinahSettings(data);
      },
    });

  const {
    data: sessionData,
    isFetching,
    isError,
    error: validationError,
  } = useQuery({
    queryKey: ["qr-verify-scan"],
    queryFn: () =>
      verifySession({ session_id: sessionId!, profile_id: profileId!, locale }),
    enabled: !!sessionId && !!profileId,
    select: ({ data }) => data,
  });

  useEffect(() => {
    setOpenErrorDialog(isError);
  }, [isError]);

  // const { data: sdkData, isFetching: isSDKFetching } = useQuery({
  //   queryKey: ["qr-binah-setting", profileId],
  //   queryFn: async () => {
  //     return (await apiClient.get("/face-scan-setting", {
  //       params: { device_type: "web", profile_id: profileId },
  //       withCredentials: true,
  //     })) as { data: BinahSetting[] };
  //   },
  //   select: ({ data }) => data,
  //   enabled: !!profileId && !!sessionData,
  //   staleTime: Infinity,
  //   gcTime: Infinity,
  // });

  const { data: userAttributes, isFetching: isAttributesFetching } = useQuery({
    queryKey: ["qr-user-attributes", profileId],
    queryFn: async () => {
      return (await apiClient.get(`/family-member-attributes`, {
        params: { profile_id: profileId, os: "web", locale },
        withCredentials: true,
      })) as { data: UserAttributes };
    },
    select: ({ data }) => data,
    enabled: !!profileId && !!sessionData,
  });

  const storeBinahSettings = async (data: BinahSetting[]) => {
    try {
      const sdkKey = data?.find(
        (setting) => setting.setting_name === "sdk_key"
      )?.setting_value;
      const scanDuration = data?.find(
        (setting) => setting.setting_name === "scan_duration"
      )?.setting_value;
      const demographicFlag = data?.find(
        (setting) => setting.setting_name === "demographic_flag"
      )?.setting_value;

      setProcessingTime(scanDuration ? +scanDuration : 0);
      setLicenseKey(sdkKey ?? "");
      setIncludeDemographic(demographicFlag === "True");
    } catch (error) {
      console.error(error);
      setProcessingTime(0);
      setLicenseKey("");
    }
  };

  useEffect(() => {
    if (profileId && sessionData) {
      fetchBinahSetting();
    }
  }, [profileId]);

  const captureUserActivity = async (
    user_activity: USER_ACTIVITY,
    ...payload: any
  ) => {
    return await apiClient.post(
      `/capture_user_activity?profile_id=${profileId}&activity_type=${user_activity}&client_name=medsi_check&locale=${locale}`,
      { binaah_key: licenseKey, timestamp: getCurrentTimestamp(), ...payload },
      { withCredentials: true }
    );
  };
  const patchScanSession = async ({
    status,
    session_id,
  }: {
    session_id: string;
    status: string;
  }) => {
    return await apiClient.patch(
      `/scan-session?os=web&profile_id=${profileId}&status=${status}&session_id=${session_id}&locale=${locale}`,
      {},
      { withCredentials: true }
    );
  };

  const { mutate, data: postHealthRes } = useMutation({
    mutationFn: async (data: any) => {
      return await apiClient.post(
        `/web_health_reading?os=web&profile_id=${profileId}&locale=${locale}`,
        data,
        { withCredentials: true }
      );
    },
    onSuccess: async (res: any) => {
      if (!res.data.success) {
        try {
          await captureUserActivity(USER_ACTIVITY.SCAN_ERROR, {
            message: imageValidityJSON,
            reading_id: sessionId,
          });
          await patchScanSession({
            session_id: sessionId || "",
            status: USER_ACTIVITY.SCAN_ERROR,
          });
        } catch (error) {
          console.error(error);
        }
        setShowErrorDialog(true);
        setIsPostingData(false);
        return;
      }

      try {
        captureUserActivity(USER_ACTIVITY.END_SCAN, {
          reading_id: sessionId,
        });
        await patchScanSession({
          session_id: sessionId || "",
          status: USER_ACTIVITY.END_SCAN,
        });
      } catch (error) {
        console.error(error);
      }
      setIsPostingData(false);
      setShowScanResult(true);
    },
    onError: (err) => {
      console.error(err);
      setShowErrorDialog(true);
      setIsPostingData(false);
    },
    onSettled: () => setIsPostingData(false),
  });

  const handleValidityJSON = (validity: string) => {
    setImageValidityJSON((prevCounts) => ({
      ...prevCounts,
      [validity]: (prevCounts[validity] || 0) + 1,
    }));
  };

  function onComplete(results: VitalSignsResults) {
    setFinalResults(results.results);
  }

  const { sessionState, vitalSigns, error, info } = useMonitor(
    videoRef as MutableRefObject<HTMLVideoElement>,
    cameraId!,
    +processingTime,
    licenseKey,
    "",
    startMeasuring,
    onComplete,
    handleValidityJSON,
    userAttributes || null,
    includeDemographic
  );

  const prevSessionState = usePrevious(sessionState);
  const errorMessage = useError(error);
  const isPageVisible = usePageVisibility();

  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState]
  );

  const durationSeconds = useTimer(isMeasuring(), +processingTime);

  const isCompleted = durationSeconds >= +processingTime;
  useEffect(() => {
    if (finalResults && isCompleted) {
      setIsPostingData(true);
      mutate({
        data: formatHealthReading(finalResults),
        scan_error: imageValidityJSON,
        timestamp: getCurrentTimestamp(),
        reading_id: sessionId,
        binaah_key: licenseKey,
      });
    }
  }, [isCompleted, finalResults]);

  const onMeasure = useCallback(async () => {
    setIsLoading(true);
    if (sessionState === SessionState.ACTIVE) {
      try {
        await patchScanSession({
          session_id: sessionId || "",
          status: USER_ACTIVITY.START_SCAN,
        });
        captureUserActivity(USER_ACTIVITY.START_SCAN, {
          reading_id: sessionId,
        });
      } catch (error) {
        console.error(error);
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message || t("invalid_session"));
          navigate(PATH_ROUTES.LOGIN);
        }
        return;
      } finally {
        setIsLoading(false);
      }

      setStartMeasuring(true);
      setLoadingTimeoutPromise(
        window.setTimeout(() => setIsLoading(true), +processingTime * 1000)
      );
    } else if (isMeasuring()) {
      try {
        captureUserActivity(USER_ACTIVITY.STOP_SCAN, {
          message: "restart session",
          reading_id: sessionId,
        });
        await patchScanSession({
          session_id: sessionId || "",
          status: USER_ACTIVITY.STOP_SCAN,
        });
      } catch (error) {
        console.error(error);
      }
      setIsLoading(false);
      clearTimeout(loadingTimeoutPromise);
      setStartMeasuring(false);
    }
  }, [sessionState, setIsLoading, processingTime]);

  useEffect(() => {
    if (isMeasuring()) {
      setIsLoading(false);
      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage
    ) {
      setIsMeasurementEnabled(false);
    }
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
  }, [errorMessage, sessionState, isPageVisible]);

  useEffect(() => {
    return () => navigate(0);
  }, []);

  if (isPostingData || isFetching || isSDKFetching || isAttributesFetching) {
    return <Loader />;
  }

  if (!profileId || !sessionId) {
    return <Navigate to={PATH_ROUTES.LOGIN} replace />;
  }

  if (showScanResult) return <FaceScanResult apiClient={apiClient} />;

  const onErrorClose = () => {
    setShowErrorDialog(false);
    navigate(0);
    navigate(`${PATH_ROUTES.FACE_SCAN}?showScan=true`);
  };

  const onRescan = () => {
    setShowErrorDialog(false);
    onMeasure();
  };

  return (
    <>
      <Scan
        info={info}
        isMeasuring={isMeasuring}
        onMeasure={onMeasure}
        videoRef={videoRef}
        vitalSigns={vitalSigns}
        isLoading={isLoading}
        processingTime={processingTime}
      />

      <ScanErrorDialog
        open={showErrorDialog}
        onClose={onErrorClose}
        onRescan={onRescan}
        error={postHealthRes?.data}
      />

      <ValidationErrorDialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
        error={validationError}
      />
    </>
  );
}
